import {
  useMemo,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { MotifSelect, MotifSelectOption, MotifFavorite } from '@ey-xd/motif-wc-react';
import { useClientStore } from '@/client/client-store';
import { useClients, useToggleFavorite } from '@/client/use-clients';
import { ClientSwitchModal } from './ClientSwitchModal';

const MAX_FAVORITES = 5;
const VISIBLE_CLIENT_ROWS = 8;
const PAGE_SIZE = 25;
const INITIAL_PAGES = 2;

let _suppressNextSelection = false;

function FavoriteCell({
  selected,
  disabled,
  onToggle,
}: {
  selected: boolean;
  disabled: boolean;
  onToggle: (favorite: boolean) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('click', stop);
    el.addEventListener('pointerdown', stop);
    el.addEventListener('mousedown', stop);
    return () => {
      el.removeEventListener('click', stop);
      el.removeEventListener('pointerdown', stop);
      el.removeEventListener('mousedown', stop);
    };
  }, []);

  return (
    <span ref={ref} style={{ display: 'inline-flex' }}>
      <MotifFavorite
        selected={selected}
        disabled={disabled}
        onMotifFavoriteToggle={(e: CustomEvent<boolean>) => {
          _suppressNextSelection = true;
          setTimeout(() => {
            _suppressNextSelection = false;
          }, 500);
          onToggle(e.detail);
        }}
      />
    </span>
  );
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        color: '#656579',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

export function ClientDropdown() {
  useClients();
  const available = useClientStore((s) => s.available);
  const current = useClientStore((s) => s.current);
  const setSwitchPopupOpen = useClientStore((s) => s.setSwitchPopupOpen);
  const toggleFavorite = useToggleFavorite();

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [selectResetNonce, setSelectResetNonce] = useState(0);

  // Force-remount MotifSelect when the active client changes externally
  // (cross-tab broadcast, fullPortalRefresh, URL-driven switch). MotifSelect
  // is a web component whose shadow-DOM selection state is not reliably cleared
  // by a React `value` prop update alone — without a key-based remount the old
  // checkmark persists alongside the new one (dual-tick bug). The modal-close
  // path already bumps the nonce; this effect covers every OTHER path that
  // updates `current` without going through the modal.
  const prevClientIdRef = useRef(current?.id ?? null);
  useEffect(() => {
    const prevId = prevClientIdRef.current;
    const nextId = current?.id ?? null;
    prevClientIdRef.current = nextId;
    // Skip the very first render (prevId === null && nextId is being set for
    // the first time) and skip when the modal flow already handled the nonce
    // bump (pendingId being cleared in onClose).
    if (prevId && nextId && prevId !== nextId) {
      setSelectResetNonce((n) => n + 1);
    }
  }, [current?.id]);

  // Pagination state - mirrors Angular's dropdownPaginated
  const [page, setPage] = useState(0);
  const [filterText, setFilterText] = useState('');

  const allFavorites = useMemo(() => [...available].filter((c) => c.isFavorite), [available]);

  const allNonFavorites = useMemo(() => [...available].filter((c) => !c.isFavorite), [available]);

  const sliceSize = PAGE_SIZE * (page + INITIAL_PAGES);

  // All items in favorites-first order.
  const allCombined = useMemo(
    () => [...allFavorites, ...allNonFavorites],
    [allFavorites, allNonFavorites],
  );

  // React owns BOTH filtering and pagination so the rendered set is fully
  // deterministic. Filter the entire client list here, then slice the matches
  // into pages. This is the only way clearing the search reliably collapses
  // back to the first virtual-scroll page: on clear `filteredAll` returns to
  // the full list and `paginatedItems` re-slices to page 0. MotifSelect's own
  // inline filter still runs over the (already-matching) rendered rows, but
  // that is a harmless no-op — every rendered option matches the current query,
  // so Motif never hides one. Because we never render more than `sliceSize`
  // rows there is also no flash of the full list while typing.
  const filteredAll = useMemo(() => {
    if (!filterText) return allCombined;
    const q = filterText.toLowerCase();
    return allCombined.filter((c) => c.name.toLowerCase().includes(q));
  }, [allCombined, filterText]);

  const paginatedItems = useMemo(() => filteredAll.slice(0, sliceSize), [filteredAll, sliceSize]);

  const favorites = useMemo(() => {
    const items = paginatedItems.filter((c) => c.isFavorite);
    // While filtering, show only rows that actually match — never force the
    // selected client in, or a no-results search still lists the current client.
    if (filterText || !current) return items;
    const currentInAvailable = available.find((c) => c.id === current.id);
    if (!currentInAvailable || !currentInAvailable.isFavorite) return items;
    const withoutCurrent = items.filter((c) => c.id !== current.id);
    return [currentInAvailable, ...withoutCurrent];
  }, [paginatedItems, current, available, filterText]);

  // Keep the selected (current) client pinned at the top of the non-favorites
  // group regardless of pagination. This prevents the selected client from
  // jumping positions when scrolling loads more pages that include it naturally.
  // Check isFavorite from `available` (source of truth) since `current` can be stale.
  const nonFavorites = useMemo(() => {
    const items = paginatedItems.filter((c) => !c.isFavorite);
    // While filtering, show only matching rows (don't force the current client).
    if (filterText || !current) return items;
    const currentInAvailable = available.find((c) => c.id === current.id);
    if (!currentInAvailable || currentInAvailable.isFavorite) return items;
    const withoutCurrent = items.filter((c) => c.id !== current.id);
    return [currentInAvailable, ...withoutCurrent];
  }, [paginatedItems, current, available, filterText]);

  // True when the selected client actually appears in a rendered group. When a
  // filter excludes it, MotifSelect loses the option node it reads the trigger
  // label from and falls back to the "Select client" placeholder — so we render
  // a hidden anchor option (below) to preserve the label.
  const currentRendered = useMemo(() => {
    if (!current) return true;
    return (
      favorites.some((c) => c.id === current.id) || nonFavorites.some((c) => c.id === current.id)
    );
  }, [favorites, nonFavorites, current]);

  const hasMore = paginatedItems.length < filteredAll.length;

  // Live ref mirrored every render so the (stable) scroll listener closure can
  // read the CURRENT pagination state without being re-created. True only when
  // the paginated slice is smaller than the (possibly filtered) full list, i.e.
  // there is genuinely another page of matches to load.
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;

  // Scroll detection: attach listener to the scrollable .motif-select-options
  // inside motif-listbox's shadow DOM (motif-select > shadow > motif-listbox > shadow > .motif-select-options)
  const scrollAttached = useRef(false);
  const selectRef = useRef<HTMLElement | null>(null);

  // Motif's `.motif-select-no-results` empty state has NO background of its own,
  // and the listbox panel host only paints white via `.motif-select-options`
  // (which is `display:none` when there are no matches). The result is a
  // transparent panel that reveals the dark header behind the "No results" text.
  // Motif exposes neither a CSS var nor a `::part` for this element, so we inject
  // a scoped rule into the listbox shadow root to give the empty state the same
  // white surface + readable text as the client list rows.
  const injectNoResultsStyles = useCallback(() => {
    const el = selectRef.current;
    const listbox = el?.shadowRoot?.querySelector('motif-listbox');
    const root = listbox?.shadowRoot;
    if (!root) return;
    // Idempotent per shadow root: guard on the root itself (not a persistent
    // ref) so a fresh listbox from a MotifSelect remount (selectResetNonce
    // bump on client switch) gets styled again instead of being skipped.
    if (root.querySelector('style[data-no-results-style]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-no-results-style', '');
    style.textContent =
      '.motif-select-no-results{background-color:var(--motif-component-select-background, #ffffff);color:var(--motif-component-select-color, #2e2e38);}';
    root.appendChild(style);
  }, []);

  // Motif's `.motif-select-options` scrollable container inside the listbox
  // shadow DOM ships NO `::-webkit-scrollbar` rules, so the scrollbar falls
  // back to the OS/browser default. Global scrollbar styles from motif-theme.scss
  // cannot cross the shadow boundary. The result is a dark/black scrollbar that
  // camouflages against the dropdown. Inject the shell's standard scrollbar
  // treatment into the listbox shadow root so it matches the rest of the app.
  const injectScrollbarStyles = useCallback(() => {
    const el = selectRef.current;
    const listbox = el?.shadowRoot?.querySelector('motif-listbox');
    const root = listbox?.shadowRoot;
    if (!root) return;
    if (root.querySelector('style[data-scrollbar-style]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-scrollbar-style', '');
    style.textContent = [
      '.motif-select-options::-webkit-scrollbar{width:0.5rem;background:transparent;}',
      '.motif-select-options::-webkit-scrollbar-thumb{background-color:#a8a8a8;border-radius:0.375rem;}',
      '.motif-select-options::-webkit-scrollbar-track{background:transparent;}',
      '.motif-select-options{scrollbar-width:thin;scrollbar-color:#a8a8a8 transparent;}',
    ].join('');
    root.appendChild(style);
  }, []);

  const attachScrollListener = useCallback(() => {
    if (scrollAttached.current) return;
    const el = selectRef.current;
    if (!el?.shadowRoot) return;

    const listbox = el.shadowRoot.querySelector('motif-listbox');
    if (!listbox?.shadowRoot) return;

    const scrollable = listbox.shadowRoot.querySelector('.motif-select-options');
    if (!scrollable) return;

    scrollAttached.current = true;
    scrollable.addEventListener('scroll', () => {
      // Advance pagination (load the next page of matches) only when there is
      // more to load. Pagination is active whether or not a filter is applied
      // because `paginatedItems` slices the FILTERED list.
      if (!hasMoreRef.current) return;
      const t = scrollable as HTMLElement;
      if (t.scrollHeight - t.scrollTop - t.clientHeight < 50) {
        setPage((p) => p + 1);
      }
    });
  }, []);

  // Reset the scroll position of the listbox's scrollable container back to
  // the top. The MotifSelect web component preserves the `.motif-select-options`
  // element across open/close cycles (hidden, not destroyed), so the browser
  // keeps the last scroll offset. Without an explicit reset the scroll bar
  // stays in the middle when the dropdown reopens.
  const resetScrollPosition = useCallback(() => {
    const el = selectRef.current;
    const listbox = el?.shadowRoot?.querySelector('motif-listbox');
    const scrollable = listbox?.shadowRoot?.querySelector('.motif-select-options');
    if (scrollable) {
      (scrollable as HTMLElement).scrollTop = 0;
    }
  }, []);

  const handleOpenChange = useCallback(
    (e: CustomEvent<boolean>) => {
      if (e.detail) {
        setPage(0);
        setFilterText('');
        scrollAttached.current = false;
        setTimeout(attachScrollListener, 100);
        setTimeout(attachScrollListener, 300);
        setTimeout(injectNoResultsStyles, 100);
        setTimeout(injectNoResultsStyles, 300);
        setTimeout(injectScrollbarStyles, 100);
        setTimeout(injectScrollbarStyles, 300);
        // Scroll the client list back to the top on every reopen
        setTimeout(resetScrollPosition, 100);
        setTimeout(resetScrollPosition, 300);
      } else {
        // Reset filter and pagination when dropdown closes so reopening
        // always shows the full unfiltered list from page 0.
        setFilterText('');
        setPage(0);
        scrollAttached.current = false;
      }
    },
    [attachScrollListener, injectNoResultsStyles, injectScrollbarStyles, resetScrollPosition],
  );

  // React renders only rows that match the current query (see `filteredAll`),
  // so every rendered option is meant to be visible. After each filter or page
  // change, clear any inline `display:none`/`aria-hidden` a previous MotifSelect
  // filter pass may have left on a reused node, then scroll back to the top so
  // the user sees the first matches (on type) or the first page (on clear).
  useLayoutEffect(() => {
    const el = selectRef.current;
    if (el) {
      el.querySelectorAll('motif-select-option:not([data-current-anchor])').forEach((opt) => {
        (opt as HTMLElement).style.display = '';
        opt.removeAttribute('aria-hidden');
      });
    }
    resetScrollPosition();
  }, [filterText, paginatedItems, resetScrollPosition]);

  const handleFilterChange = useCallback(
    (e: CustomEvent<{ value: string; visibleCount: number }>) => {
      setFilterText(e.detail.value);
      setPage(0);
      // Re-attach scroll listener after filter changes DOM content
      scrollAttached.current = false;
      setTimeout(attachScrollListener, 100);
    },
    [attachScrollListener],
  );

  // Motif's filter clear ("X") button does not reliably emit `filterChange`, so
  // React's `filterText` can stay stale after a clear — the dropdown then keeps
  // filtering and shows nothing (or only the pinned current client). Read the
  // real filter input value after any click inside the control and sync it, so
  // clearing always restores the full paginated list. Re-runs on remount.
  useEffect(() => {
    const host = selectRef.current;
    if (!host) return;
    const syncFromInput = () => {
      requestAnimationFrame(() => {
        const el = selectRef.current;
        const sr = el?.shadowRoot;
        const input =
          (sr?.querySelector('input') as HTMLInputElement | null) ??
          (sr
            ?.querySelector('motif-listbox')
            ?.shadowRoot?.querySelector('input') as HTMLInputElement | null) ??
          null;
        const value = input ? input.value : '';
        setFilterText(value);
        setPage(0);
      });
    };
    host.addEventListener('click', syncFromInput);
    return () => host.removeEventListener('click', syncFromInput);
  }, [selectResetNonce]);

  const favouritesCount = available.filter((c) => c.isFavorite).length;

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <label
          htmlFor="header-client-select"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Client
        </label>
        <MotifSelect
          key={`client-select-${selectResetNonce}`}
          ref={(el: HTMLElement | null) => {
            selectRef.current = el;
          }}
          id="header-client-select"
          value={current?.id ?? ''}
          visibleOptions={VISIBLE_CLIENT_ROWS}
          placeholder="Select client"
          filterable
          filterPlaceholder="Search client"
          onOpenChange={handleOpenChange}
          onFilterChange={handleFilterChange}
          onSelectionChanged={(e: CustomEvent<string | string[]>) => {
            if (_suppressNextSelection) {
              _suppressNextSelection = false;
              return;
            }
            const nextId = Array.isArray(e.detail) ? e.detail[0] : e.detail;
            if (nextId?.startsWith('__group_')) return;
            // if (nextId?.startsWith('dummy-client-')) return; // disabled for debug testing
            if (!nextId || nextId === current?.id) return;
            setPendingId(nextId);
            setSwitchPopupOpen(true);
          }}
          style={
            {
              minWidth: 270,
              ['--dropdown-menu-item-selected-check-icon-color']: 'transparent',
            } as React.CSSProperties
          }
        >
          {favorites.length > 0 && (
            <>
              <MotifSelectOption disabled value="__group_favorites__">
                <GroupLabel>Favorite Clients</GroupLabel>
              </MotifSelectOption>
              {favorites.map((c) => (
                <MotifSelectOption key={c.id} value={c.id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FavoriteCell
                      selected={c.isFavorite}
                      disabled={!c.isFavorite && favouritesCount >= MAX_FAVORITES}
                      onToggle={(favorite) => toggleFavorite.mutate({ id: c.id, favorite })}
                    />
                    <span>{c.name}</span>
                  </span>
                </MotifSelectOption>
              ))}
            </>
          )}
          {nonFavorites.length > 0 && (
            <>
              <MotifSelectOption disabled value="__group_clients__">
                <GroupLabel>Clients</GroupLabel>
              </MotifSelectOption>
              {nonFavorites.map((c) => (
                <MotifSelectOption key={c.id} value={c.id}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FavoriteCell
                      selected={c.isFavorite}
                      disabled={!c.isFavorite && favouritesCount >= MAX_FAVORITES}
                      onToggle={(favorite) => toggleFavorite.mutate({ id: c.id, favorite })}
                    />
                    <span>{c.name}</span>
                  </span>
                </MotifSelectOption>
              ))}
            </>
          )}
          {hasMore && (
            <MotifSelectOption disabled value="__loading__">
              <span style={{ fontSize: '0.75rem', color: '#999' }}>Loading more...</span>
            </MotifSelectOption>
          )}
          {current && !currentRendered && (
            <MotifSelectOption
              key={`current-anchor-${current.id}`}
              value={current.id}
              data-current-anchor=""
              aria-hidden="true"
              style={{ display: 'none' }}
            >
              <span>{current.name}</span>
            </MotifSelectOption>
          )}
        </MotifSelect>
      </div>

      <ClientSwitchModal
        pendingClientId={pendingId}
        onClose={(confirmed) => {
          setPendingId(null);
          setSwitchPopupOpen(false);
          // Only remount MotifSelect when the client actually changed (confirm
          // path). On cancel the selected client is unchanged, so a remount is
          // unnecessary and causes two bugs:
          //   1. A brief "Select client" placeholder flash while the new web
          //      component instance hydrates.
          //   2. A corrupted internal filter state in the fresh MotifSelect
          //      instance that can leave most options hidden after a subsequent
          //      paste-then-clear sequence.
          if (confirmed) {
            setSelectResetNonce((nonce) => nonce + 1);
          }
        }}
      />
    </>
  );
}
